import { Link } from "react-router-dom";
import {
    ArrowLeft, Minus, Plus, ShoppingBag, BadgePercent, X, MapPin, Home, Briefcase,
    Banknote, CreditCard, Loader2, ChevronRight, AlertTriangle, Coins, Wallet,
} from "lucide-react";
import { inr2 } from "../../lib/api";
import { t } from "../../lib/i18n";
import { FullPageLoader } from "../../components/Loaders";
import AddressPickerModal from "../../components/AddressPickerModal";
import CouponPickerModal from "./_partials/CouponPickerModal";
import { AddressPickerDrawer, CoinsRedeemBlock } from "./_partials/CartPickers";
import { coinRedeemCap, coinRedeemMin } from "../../lib/coinRedemption";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { useCartController } from "./controllers/useCartController";
import BkCartView from "./blinkit/BkCartView";
import DeliveryOptions from "../../components/lse/DeliveryOptions";
import OfflineCartNotice from "../../components/lse/OfflineCartNotice";
import { deliveryBlocking } from "../../lib/lse";
import { cartShopLabel } from "../../lib/multiShop";
import { ShopBreakdown, ShopDivider } from "../../components/MultiShop";

const LABEL_ICON = { home: Home, work: Briefcase };

export default function CartPage() {
    return useIsBlinkit() ? <BkCartView /> : <ClassicCartPage />;
}

function ClassicCartPage() {
    const {
        lang, cart, code, setCode, coupon, busy, addrs, picker, setPicker, showAddr, setShowAddr, selectedAddrId,
        setSelectedAddrId, payMethods, payment, setPayment, placing, showCouponPicker, setShowCouponPicker, availCoupons,
        couponPickerBusy, coinsBalance, coinsConfig, coinsToRedeem, setCoinsToRedeem, useWallet, setUseWallet,
        walletBalance, loadAddrs, selectedAddr, change, apply, removeCoupon, openCouponPicker, applyCouponFromPicker,
        placeOrder, lse, delivery, chooseDelivery, offline,
    } = useCartController();
    const lseBlock = deliveryBlocking(lse);

    if (!cart) return <FullPageLoader />;
    const items = cart.items || [];

    if (items.length === 0) {
        return (
            <div
                className="flex flex-col items-center justify-center px-6 text-center"
                // iter146 — was `min-h-screen` (100vh), independently
                // stacked on top of two ancestor wrappers (`.pwa-bg`,
                // `.pwa-shell`) that ALSO each claimed their own full
                // 100vh, nested inside `body`'s already safe-area-
                // padded box. Net result on notched iOS devices: the
                // page grew taller than the actual viewport (phantom
                // scrollbar, nothing real to scroll to) and this block
                // centered itself inside its own oversized box, landing
                // visibly below true screen-centre (user-reported
                // screenshot — empty Cart). All three layers now share
                // the same safe-area-aware calc so the fill target
                // matches what's really left on screen. See the
                // matching `.pwa-shell` comment in index.css for the
                // full writeup.
                style={{ minHeight: "calc(100dvh - var(--safe-top, 0px) - var(--safe-bottom, 0px))" }}
            >
                <img src="https://static.prod-images.emergentagent.com/jobs/7f3498e8-faec-4d33-910a-2c5011a868fc/images/f49d64b6e17182fecbc00f1f39bfbfadca651109a115c8d1ab50683dfad1af4e.png" alt="Empty cart" className="w-56 h-56 object-contain" />
                <h2 className="text-xl font-bold mt-4">{t("cart_empty", lang)}</h2>
                <Link to="/" data-testid="browse-restaurants" className="mt-6 bg-primary text-primary-foreground rounded-full px-6 py-3 font-semibold">{t("cart_browse", lang)}</Link>
            </div>
        );
    }

    const totals = cart.totals || {};
    // ``totals.grand_total`` from the server already reflects coupon +
    // coin redemption, so we don't subtract ``discount`` again here.
    const grand = totals.grand_total || 0;
    const couponDisc = Number(totals.coupon_discount || 0);
    const coinsDisc = Number(totals.coins_discount || 0);
    const AddrIcon = (selectedAddr && LABEL_ICON[selectedAddr.label]) || MapPin;

    // Cap on coins the customer can burn this order: lesser of balance
    // and the configured % of subtotal (after coupon, except for FREE_DELIVERY).
    const coinMax = coinRedeemCap(coinsConfig, coinsBalance, totals);
    const coinMin = coinRedeemMin(coinsConfig);

    return (
        <div className="pb-32 lg:pb-12">
            <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card sticky top-0 z-10 border-b border-border">
                <Link to="/" className="p-1 -ml-1"><ArrowLeft size={22} /></Link>
                <div>
                    <h1 className="text-base font-bold">{cartShopLabel(cart)}</h1>
                    <p className="text-xs text-muted-foreground">{items.reduce((s, i) => s + i.quantity, 0)} items</p>
                </div>
            </header>

            {/* Location Service Engine offline cart: first thing on screen, since the page is view-only until reconnected. */}
            {offline && <div className="mt-3 mx-4"><OfflineCartNotice savedAt={cart?._offline_saved_at} /></div>}

            {/* Tablet+ split: items + address on the left, rewards +
                payment + totals on the right so the cart no longer reads
                as one long vertical strip on iPad/desktop. */}
            <div className="lg:grid lg:grid-cols-[1.4fr_1fr] lg:gap-4 lg:px-4 lg:pt-4">
            <div className="lg:space-y-3">

            <div className="px-4 lg:px-0 mt-2 lg:mt-0 space-y-1 bg-card lg:rounded-2xl lg:border lg:border-border lg:p-4">
                {items.map((it, idx) => [
                    // Multi-shop checkout only (renders nothing otherwise): a heading before each shop's items.
                    <ShopDivider key={`shop-${it.product_id}`} cart={cart} items={items} index={idx} />,
                    <div key={it.product_id} className="flex items-center gap-3 py-3 border-b last:border-0 border-border" data-testid={`cart-item-${it.product_id}`}>
                        <span className={`food-mark ${it.food_type === "NON_VEG" ? "non-veg" : "veg"}`} />
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm line-clamp-1">{it.name}</p>
                            <p className="text-xs text-muted-foreground inline-flex items-baseline gap-1.5">
                                <span>{inr2(it.selling_price)} each</span>
                                {it.mrp && it.mrp > it.selling_price && (
                                    <span className="line-through opacity-70" data-testid={`cart-mrp-${it.product_id}`}>{inr2(it.mrp)}</span>
                                )}
                            </p>
                        </div>
                        <div className="flex items-center bg-card border-2 border-primary rounded-xl overflow-hidden">
                            <button data-testid={`cart-dec-${it.product_id}`} onClick={() => change(it, -1)} className="px-2 py-1 text-primary"><Minus size={14} /></button>
                            <span className="px-2 text-primary font-bold text-sm">{it.quantity}</span>
                            <button data-testid={`cart-inc-${it.product_id}`} onClick={() => change(it, 1)} className="px-2 py-1 text-primary"><Plus size={14} /></button>
                        </div>
                        <div className="w-16 text-right text-sm font-semibold">{inr2(it.line_total)}</div>
                    </div>,
                ])}
            </div>

            {/* Multi-shop checkout only (renders nothing otherwise): what each shop's order comes to. */}
            <ShopBreakdown cart={cart} className="mt-3 lg:mt-0 mx-4 lg:mx-0" />

            {/* Delivery address re-confirm */}
            <section className="mt-4 lg:mt-0 mx-4 lg:mx-0 bg-card rounded-2xl border border-border p-4" data-testid="cart-address">
                <div className="flex items-start gap-3">
                    <AddrIcon size={20} className="text-primary mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Deliver to</p>
                        {selectedAddr ? (
                            <>
                                <p className="font-bold text-sm capitalize">{selectedAddr.label}</p>
                                <p className="text-xs text-muted-foreground line-clamp-2">{selectedAddr.line1}{selectedAddr.line2 ? `, ${selectedAddr.line2}` : ""} • {selectedAddr.city} {selectedAddr.pincode}</p>
                            </>
                        ) : (
                            <p className="text-sm font-semibold text-muted-foreground">No address set</p>
                        )}
                    </div>
                    <button data-testid="addr-change" onClick={() => setShowAddr(true)} className="text-xs font-bold text-primary uppercase tracking-wider inline-flex items-center gap-1">Change <ChevronRight size={12} /></button>
                </div>
                {totals.delivery_out_of_zone && (
                    <div
                        data-testid="cart-out-of-zone-banner"
                        className="mt-3 bg-rose-50 border border-rose-300 text-rose-800 rounded-xl p-3 flex items-start gap-2"
                    >
                        <AlertTriangle size={16} className="text-rose-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 text-xs">
                            <p className="font-bold">This shop doesn&apos;t deliver to this address.</p>
                            <p className="mt-0.5 opacity-90">
                                It&apos;s <strong>{totals.delivery_distance_km} km away</strong> — the shop only delivers within <strong>{totals.delivery_zone_radius_km} km</strong>. Pick a closer address to place the order.
                            </p>
                            <button
                                data-testid="cart-out-of-zone-change"
                                onClick={() => setShowAddr(true)}
                                className="mt-2 bg-rose-600 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold inline-flex items-center gap-1"
                            >Pick a different address <ChevronRight size={11} /></button>
                        </div>
                    </div>
                )}
            </section>

            </div>
            {/* Right column at lg+ — payment + rewards + totals. */}
            <div className="lg:space-y-3">

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            {lse && <div className="mt-3 lg:mt-0 mx-4 lg:mx-0"><DeliveryOptions lse={lse} value={delivery} onChange={chooseDelivery} /></div>}

            {/* Payment method */}
            <section className="mt-3 lg:mt-0 mx-4 lg:mx-0 bg-card rounded-2xl border border-border p-4" data-testid="cart-payment">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Payment</p>

                {/* iter67 — Wallet as a payment SOURCE. Shown when the
                    customer has any balance. Checking the toggle drains
                    the wallet up to the grand total; the COD/Online
                    picker collapses to a thin "remaining via …" hint
                    when the wallet fully covers the order. */}
                {walletBalance > 0 && (() => {
                    const walletCovers = walletBalance >= grand;
                    const walletApplied = useWallet ? Math.min(walletBalance, grand) : 0;
                    const remaining = Math.max(0, grand - walletApplied);
                    return (
                        <div className="mt-2 rounded-xl border border-primary/30 bg-primary/5 overflow-hidden" data-testid="wallet-source-section">
                            <label className="flex items-center justify-between px-3 py-3 cursor-pointer">
                                <span className="inline-flex items-center gap-3">
                                    <Wallet size={18} className="text-primary" />
                                    <span>
                                        <span className="text-sm font-bold block">Use my Wallet</span>
                                        <span className="text-[11px] text-muted-foreground">
                                            Balance {inr2(walletBalance)}{walletCovers ? " — covers this order" : ""}
                                        </span>
                                    </span>
                                </span>
                                <input
                                    data-testid="use-wallet"
                                    type="checkbox"
                                    checked={useWallet}
                                    onChange={(e) => setUseWallet(e.target.checked)}
                                    className="w-5 h-5 accent-primary"
                                />
                            </label>
                            {useWallet && (
                                <div className="px-3 pb-3 pt-1 text-[12px] border-t border-primary/20 bg-card" data-testid="wallet-split">
                                    <div className="flex items-center justify-between py-1">
                                        <span className="text-muted-foreground">Wallet pays</span>
                                        <span className="font-bold text-primary" data-testid="wallet-amount">−{inr2(walletApplied)}</span>
                                    </div>
                                    {!walletCovers && (
                                        <div className="flex items-center justify-between py-1">
                                            <span className="text-muted-foreground">Remaining via {payment === "COD" ? "Cash" : "Online"}</span>
                                            <span className="font-bold" data-testid="wallet-remaining">{inr2(remaining)}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Hide the COD/Online picker entirely when the wallet
                    covers the whole order. */}
                {!(useWallet && walletBalance >= grand) && (
                    <div className="grid grid-cols-1 gap-2 mt-2">
                        {payMethods.cod && (
                            <PayOption data-testid="pay-cod" Icon={Banknote} active={payment === "COD"} onClick={() => setPayment("COD")}
                                label="Cash on Delivery" hint="Pay with cash when your order arrives" />
                        )}
                        {payMethods.online && (
                            <PayOption data-testid="pay-online" Icon={CreditCard} active={payment === "ONLINE"} onClick={() => setPayment("ONLINE")}
                                label="Online · UPI / Cards" hint={payMethods.razorpay_mock ? "Mocked — payment will auto-succeed for testing" : "Razorpay"} />
                        )}
                        {!payMethods.cod && !payMethods.online && (
                            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">No payment methods available for this location.</p>
                        )}
                    </div>
                )}
            </section>

            {/* Coupons + Coins */}
            <section className="mt-3 lg:mt-0 mx-4 lg:mx-0 bg-card rounded-2xl p-4 border border-border" data-testid="rewards-section">
                <div className="flex items-center gap-2 mb-3">
                    <BadgePercent size={18} className="text-primary" />
                    <h3 className="font-bold text-sm">Offers &amp; Rewards</h3>
                </div>
                {coupon ? (
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
                        <div className="text-sm">
                            <p className="font-bold text-emerald-700 font-mono">{coupon.code}</p>
                            <p className="text-xs text-emerald-700">Saved {inr2(couponDisc)}</p>
                        </div>
                        <button data-testid="remove-coupon" onClick={removeCoupon} className="text-emerald-700"><X size={18} /></button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <button
                            type="button"
                            data-testid="coupon-browse"
                            onClick={openCouponPicker}
                            className="w-full flex items-center justify-between gap-2 px-3 py-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl text-left transition"
                        >
                            <span className="inline-flex items-center gap-2">
                                <BadgePercent size={16} className="text-amber-600" />
                                <span className="text-sm font-semibold text-amber-800">View available coupons</span>
                            </span>
                            <ChevronRight size={16} className="text-amber-600" />
                        </button>
                        <div className="flex gap-2">
                            <input data-testid="coupon-input" placeholder="Or enter code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="flex-1 bg-muted rounded-xl px-3 py-2.5 text-sm font-semibold uppercase tracking-wider outline-none" />
                            <button data-testid="apply-coupon-btn" onClick={apply} disabled={busy || !code} className="bg-foreground text-background px-4 rounded-xl font-semibold text-sm disabled:opacity-50">Apply</button>
                        </div>
                    </div>
                )}

                {/* Coins redeem block — hidden when the tenant disabled coins or balance is 0 */}
                <CoinsRedeemBlock
                    coinsBalance={coinsBalance}
                    coinsConfig={coinsConfig}
                    coinsToRedeem={coinsToRedeem}
                    setCoinsToRedeem={setCoinsToRedeem}
                    coinMax={coinMax}
                    coinMin={coinMin}
                    coinsDisc={coinsDisc}
                    subtotalForCap={Math.max(0, (totals.subtotal || 0) - (totals.coupon_kind === "FREE_DELIVERY" ? 0 : couponDisc))}
                />
            </section>

            {/* Totals */}
            <section className="mt-3 lg:mt-0 mx-4 lg:mx-0 bg-card rounded-2xl p-4 border border-border space-y-2 text-sm">
                <Row k={t("subtotal", lang)} v={inr2(totals.subtotal)} />
                {totals.packaging_fee > 0 && <Row k="Packaging" v={inr2(totals.packaging_fee)} />}
                <Row k={t("delivery_fee", lang)} v={
                    totals.delivery_fee === 0 ? (
                        <span className="inline-flex items-center gap-1.5">
                            {totals.delivery_fee_rider > 0 && (
                                <span className="text-muted-foreground line-through text-xs" data-testid="delivery-fee-strikethrough">{inr2(totals.delivery_fee_rider)}</span>
                            )}
                            <span className="text-emerald-600 font-semibold" data-testid="delivery-fee-free-badge">FREE</span>
                        </span>
                    ) : inr2(totals.delivery_fee)
                } />
                {totals.delivery_fee === 0 && totals.free_delivery_source && totals.delivery_fee_rider > 0 && (
                    <p className="text-[11px] text-emerald-700 -mt-1 px-1" data-testid="delivery-fee-free-reason">
                        {totals.free_delivery_source === "ZONE" && "Free delivery in this zone"}
                        {totals.free_delivery_source === "SHOP" && "Free delivery from this shop"}
                        {totals.free_delivery_source === "COUPON" && "Free delivery (coupon applied)"}
                    </p>
                )}
                {totals.delivery_distance_km != null && totals.delivery_fee > 0 && (
                    <p className="text-[11px] text-muted-foreground -mt-1 px-1" data-testid="delivery-fee-breakdown">
                        {totals.delivery_distance_km} km · ₹{totals.delivery_base_fee} base + ₹{totals.delivery_per_km_fee}/km extra
                    </p>
                )}
                {totals.gst_and_other_charges > 0 && (
                    <Row k={<span title={"Item GST + Delivery GST + Additional charges"} className="cursor-help underline decoration-dotted">GST and Other Charges</span>} v={inr2(totals.gst_and_other_charges)} />
                )}
                {couponDisc > 0 && (
                    <Row k={<span className="inline-flex items-center gap-1"><BadgePercent size={12} className="text-emerald-600" />Coupon ({coupon?.code})</span>}
                        v={<span className="text-emerald-600" data-testid="bill-coupon-discount">- {inr2(couponDisc)}</span>} />
                )}
                {coinsDisc > 0 && (
                    <Row k={<span className="inline-flex items-center gap-1"><Coins size={12} className="text-amber-500" />Coins redeemed</span>}
                        v={<span className="text-emerald-600" data-testid="bill-coins-discount">- {inr2(coinsDisc)}</span>} />
                )}
                <div className="border-t border-border pt-2 mt-2 flex items-center justify-between font-bold text-base">
                    <span>{t("to_pay", lang)}</span>
                    <span data-testid="grand-total">{inr2(grand)}</span>
                </div>
            </section>

            {/* Place order — iter178: below lg this is a ``fixed`` bottom bar, so where it sits in the DOM changes nothing on phones and tablets;
                from lg it is static — the last thing in the summary column, never floating over the bill. */}
            <div
                className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full z-40 bg-card border-t border-border px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:static lg:translate-x-0 lg:z-auto lg:!max-w-none lg:bg-transparent lg:border-0 lg:p-0"
                style={{ maxWidth: "var(--shell-width)" }}
            >
                {(() => {
                    const walletFullyCovers = useWallet && walletBalance >= grand;
                    const walletApplied = useWallet ? Math.min(walletBalance, grand) : 0;
                    const remaining = Math.max(0, grand - walletApplied);
                    // When wallet fully covers, we don't require a
                    // secondary method. Otherwise the user must pick COD
                    // or Online for the remaining amount.
                    const needsMethod = !walletFullyCovers;
                    const disabled = placing
                        || !selectedAddr
                        || (needsMethod && !payment)
                        || (needsMethod && !payMethods.cod && !payMethods.online)
                        || totals.delivery_out_of_zone
                        || !!lseBlock || offline;
                    return (
                        <button
                            data-testid="place-order"
                            disabled={disabled}
                            onClick={placeOrder}
                            className="w-full bg-primary text-primary-foreground rounded-2xl py-4 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {placing ? <Loader2 size={18} className="animate-spin" /> : <ShoppingBag size={18} />}{" "}
                            {totals.delivery_out_of_zone
                                ? "Out of delivery zone"
                                : walletFullyCovers
                                    ? <>Place Order • Wallet {inr2(grand)}</>
                                    : <>Place Order • {inr2(grand)}</>}
                        </button>
                    );
                })()}
            </div>
            </div>
            </div>


            {/* Coupon picker modal */}
            {showCouponPicker && (
                <CouponPickerModal
                    busy={busy}
                    available={availCoupons}
                    loading={couponPickerBusy}
                    onApply={applyCouponFromPicker}
                    onClose={() => setShowCouponPicker(false)}
                />
            )}

            {/* Address picker drawer */}
            {showAddr && (
                <AddressPickerDrawer
                    addrs={addrs}
                    selectedAddrId={selectedAddrId}
                    onPick={(id) => { setSelectedAddrId(id); setShowAddr(false); }}
                    onAddNew={() => { setShowAddr(false); setPicker(true); }}
                    onClose={() => setShowAddr(false)}
                />
            )}

            {picker && (
                <AddressPickerModal onClose={() => setPicker(false)} onSaved={async (saved) => {
                    setPicker(false);
                    await loadAddrs();
                    // Auto-select the just-added address so the customer doesn't have to pick again
                    if (saved?.id) setSelectedAddrId(saved.id);
                }} />
            )}
        </div>
    );
}

function Row({ k, v }) {
    return <div className="flex items-center justify-between"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
}

function PayOption({ Icon, active, onClick, label, hint, ...rest }) {
    return (
        <button {...rest} onClick={onClick}
            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition ${active ? "border-primary bg-primary/5" : "border-border"}`}>
            <Icon size={20} className={active ? "text-primary" : "text-muted-foreground"} />
            <div className="flex-1 text-left">
                <p className="font-semibold text-sm">{label}</p>
                <p className="text-xs text-muted-foreground">{hint}</p>
            </div>
            <span className={`w-4 h-4 rounded-full border-2 ${active ? "border-primary bg-primary" : "border-muted-foreground"}`} />
        </button>
    );
}
